import { MutationKey, QueryKey } from '@tanstack/react-query'
import { DefinitionNode, DocumentNode, OperationDefinitionNode } from 'graphql'
import { set } from 'lodash'
import { FieldErrors, FieldValues } from 'react-hook-form'

import { MutationInputError } from './errors'
import { PaginationVariables } from './table'

export { gql } from 'graphql-tag'

export const getKey = (documentNode: DocumentNode): MutationKey | QueryKey => {
  const operationNode: OperationDefinitionNode | undefined = documentNode.definitions.find(
    (def: DefinitionNode) => def.kind === 'OperationDefinition',
  )

  if (operationNode === undefined) {
    throw Error('DocumentNode should have an OperationDefinitionNode')
  } else if (operationNode.name === undefined) {
    throw Error('OperationDefinitionNode should have a name')
  } else {
    return [operationNode.name.value]
  }
}

/**
 * Map GraphQL mutation errors to `react-hook-form` errors
 */
export const mapGraphQLErrors = <T extends FieldValues>(
  graphQLErrors: MutationInputError[],
  attributeReplacer?: (attribute: MutationInputError['attribute']) => string,
): FieldErrors<T> => {
  const result: FieldErrors<T> = {}

  Object.assign(
    result,
    ...graphQLErrors.map((error: MutationInputError) => {
      const key: MutationInputError['attribute'] = attributeReplacer
        ? attributeReplacer(error.attribute)
        : error.attribute

      const { message } = error
      if (key.includes('.')) {
        // convert path string (e.g. 'foo[2].bar[3]') into object structure
        set(result, key, { message })
      } else {
        return {
          [key]: { message },
        }
      }
    }),
  )

  return result
}

export const calculatePagination = ({
  page,
  rowsPerPage,
}: {
  page: number
  rowsPerPage: number
}): PaginationVariables => ({
  first: page * rowsPerPage,
  last: rowsPerPage,
})

export const permitFields = <T>(fieldAccessObject: Partial<Record<keyof T, boolean>>): string[] =>
  Object.entries(fieldAccessObject)
    .filter(([, value]) => value)
    .map(([key]) => key)

export const fieldsToOmit = <T>(entry: Partial<Record<keyof T, boolean>>): string[] =>
  Object.keys(entry).filter((key: string): boolean => {
    const condition: boolean | undefined = entry[key as keyof typeof entry]

    if (condition === undefined) {
      return false
    } else {
      return condition
    }
  })
